import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["data-index"]

import { GalleryItem } from '@/store/gallery/types'
import {
  nextTick,
  onMounted,
  ref,
  watch
} from 'vue'

type Column = number[]


export default /*#__PURE__*/_defineComponent({
  __name: 'MosaicGrid',
  props: {
    numCols: { default: 1 },
    items: null,
    gap: { default: 0 }
  },
  setup(__props: any) {

const props = __props as { numCols: number, items: GalleryItem[], gap: number };



const columns = ref<Column[]>([])
const gridRef = ref<HTMLDivElement | null>(null)
const colWidth = ref(0)

onMounted(() => {
  // This observer is required to calculate the column width based of the size of the grid. 
  // getBoundingClientRect returns zero if display:none, so we need to set the colWidth on the 
  // first instance when width is not zero.
  if (gridRef.value) {
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.contentRect.width) {
          // Ensure the boundingClientRect width is not zero
          const newColWidth = (entry.contentRect.width + props.gap) / props.numCols - props.gap
          
          // Only redraw when the colWidth has changed
          if (newColWidth !== colWidth.value){
            colWidth.value = newColWidth 
            redraw()            
          }
        }
      })
    }).observe(gridRef.value)
  }

})

watch(() => props.items.length, async (newItemsLength, prevItemsLength) => {
  if (columns.value.length > 0 && newItemsLength >  prevItemsLength) {
    // Columns need to have at least one item rendered to calculate the target to render the next one. 
    await fillColumns(prevItemsLength)
  } else {
    redraw()
  }
})

const redraw = async () => {
  columns.value = createColumns()
  await fillColumns(0)
}

const createColumns = (): Column[] => {
  return [...new Array(props.numCols)].map(() => [])
}

/**
 * Recursively add item to shortest column
 * @param itemIndex
 */
const fillColumns = async (itemIndex: number) => {
  if (itemIndex >= props.items.length) {
    return
  }

  // Wait for columns divs to be rendered
  await nextTick()
  if (gridRef.value) {
    const columnDivs = [...gridRef.value.children] as HTMLDivElement[]

    const target = columnDivs.reduce((prev, curr) =>
      curr.getBoundingClientRect().height < prev.getBoundingClientRect().height
        ? curr
        : prev
    )
    if (target.dataset.index !== undefined) {
      columns.value[+target.dataset.index].push(itemIndex)
      await fillColumns(itemIndex + 1)
    }
  }
}

/**
 *  Required to get the correct aspect ratio as preview video can have a different aspect ratio from the actual video
 */
const widthToHeightRatio = (item: GalleryItem) => {
  if (item.preview.videoSource) {
    if (item.preview.videoSource.height && item.preview.videoSource.width) {
      return item.preview.videoSource.width / item.preview.videoSource.height
    }
  }
  return item.widthToHeightRatio
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "gridRef",
    ref: gridRef,
    class: "mosaic-grid",
    style: _normalizeStyle({ gap: `${__props.gap}px` })
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column, columnIndex) => {
      return (_openBlock(), _createElementBlock("div", {
        key: columnIndex,
        class: "mosaic-grid__column",
        "data-index": columnIndex,
        style: _normalizeStyle({ gap: `${__props.gap}px` })
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(column, (itemIndex) => {
          return (_openBlock(), _createElementBlock("div", {
            key: itemIndex,
            class: "mosaic-grid__item",
            style: _normalizeStyle(colWidth.value ? { height: colWidth.value / widthToHeightRatio(__props.items[itemIndex]) + 'px' } : undefined)
          }, [
            _renderSlot(_ctx.$slots, "default", {
              item: (__props.items[itemIndex]),
              index: itemIndex
            })
          ], 4))
        }), 128))
      ], 12, _hoisted_1))
    }), 128))
  ], 4))
}
}

})